import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QDivider, QLoader, QMenu, QText, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { ConversationListItem } from '@captivateiq/assistant/types';
import { CONVERSATIONS_PAGE_SIZE, MAX_DROPDOWN_CONVERSATIONS } from './ConversationHistory/constants';
import { filterConversationsBySearch } from './ConversationHistory/filterConversationsBySearch';
import { GroupedConversationList } from './ConversationHistory/GroupedConversationList';

type ChatHistoryDropdownProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
};

/** Distance (px) from the bottom of the scroll container that triggers loading the next page. */
const SCROLL_LOAD_MORE_THRESHOLD_PX = 48;

export const ChatHistoryDropdown = ({ anchorEl, open, onClose }: ChatHistoryDropdownProps) => {
  const { t } = useTranslation('assistant');
  const { getConversations, openAllChats } = useAIShellContext();

  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Separate from `hasError` (the initial-load failure state, which replaces the whole panel with
  // an error message): a failed load-more should leave the already-loaded list visible and let the
  // user retry by scrolling again, not look identical to the list never having loaded at all.
  const [hasLoadMoreError, setHasLoadMoreError] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Synchronous lock: state updates batch, so a fast second scroll event can fire before
  // `isLoadingMore` re-renders. A ref closes that window; the state is still used for the spinner.
  const isLoadingMoreRef = useRef(false);
  // Mirror `conversations.length`/`totalCount` synchronously and read them from `handleScroll`'s
  // body rather than its closure — the closure is only recreated on the next render, which can
  // still be stale in the instant right after `isLoadingMoreRef` unlocks, otherwise causing a
  // redundant (or, without the length ref, duplicate) fetch for a page already in flight.
  const conversationsLengthRef = useRef(0);
  const totalCountRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    setHasLoadMoreError(false);
    setQuery('');
    (async () => {
      try {
        const result = await getConversations(CONVERSATIONS_PAGE_SIZE, 0);
        if (cancelled) return;
        const data = result?.data ?? [];
        setConversations(data);
        conversationsLengthRef.current = data.length;
        const total = result?.total_count ?? 0;
        setTotalCount(total);
        totalCountRef.current = total;
      } catch (error) {
        if (cancelled) return;
        console.error('ChatHistoryDropdown: Error fetching conversations:', error);
        setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, getConversations]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const hasMore =
        conversationsLengthRef.current < totalCountRef.current &&
        conversationsLengthRef.current < MAX_DROPDOWN_CONVERSATIONS;
      if (isLoadingMoreRef.current || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - (scrollTop + clientHeight) > SCROLL_LOAD_MORE_THRESHOLD_PX) return;

      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      setHasLoadMoreError(false);
      getConversations(CONVERSATIONS_PAGE_SIZE, conversationsLengthRef.current)
        .then((result) => {
          const newData = result?.data ?? [];
          setConversations((prev) => {
            const next = [...prev, ...newData];
            conversationsLengthRef.current = next.length;
            return next;
          });
          const total = result?.total_count ?? totalCountRef.current;
          setTotalCount(total);
          totalCountRef.current = total;
        })
        .catch((error) => {
          console.error('ChatHistoryDropdown: Error loading more conversations:', error);
          setHasLoadMoreError(true);
        })
        .finally(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        });
    },
    [getConversations],
  );

  const filtered = useMemo(() => filterConversationsBySearch(conversations, query), [conversations, query]);

  const handleViewAllChats = useCallback(() => {
    openAllChats();
    onClose();
  }, [openAllChats, onClose]);

  return (
    <QMenu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      autoFocus={false}
      disableAutoFocusItem
      open={open}
      slotProps={{
        paper: { sx: { width: '360px' } },
        list: { sx: { padding: 0 } },
        transition: { onEntered: () => searchInputRef.current?.focus() },
      }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClose={onClose}
    >
      <QBox sx={{ padding: '8px 12px' }}>
        <QTextField
          fullWidth
          inputRef={searchInputRef}
          placeholder={t('input_placeholders.search_chats_count', { count: totalCount }) as string}
          size="small"
          startIcon={faMagnifyingGlass}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </QBox>
      <QBox
        data-testid="chat-history-scroll-container"
        sx={{
          maxHeight: '320px',
          overflowY: 'auto',
          padding: '4px 8px',
          '&::-webkit-scrollbar': { background: 'transparent', width: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'neutral.primary.200', borderRadius: '4px' },
        }}
        onScroll={handleScroll}
      >
        {isLoading && (
          <QBox sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
            <QLoader />
          </QBox>
        )}
        {!isLoading && hasError && (
          <QText sx={{ padding: '8px 12px', color: 'text.onSurface.background.mediumContrast' }}>
            {t('messages.error_loading_chats')}
          </QText>
        )}
        {!isLoading && !hasError && (
          <>
            <GroupedConversationList
              conversations={filtered}
              emptyState={
                <QText sx={{ padding: '8px 12px', color: 'text.onSurface.background.mediumContrast' }}>
                  {t(query ? 'messages.no_chats_match_search' : 'messages.no_chats')}
                </QText>
              }
              onSelect={onClose}
            />
            {isLoadingMore && (
              <QBox sx={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                <QLoader />
              </QBox>
            )}
            {hasLoadMoreError && (
              <QText sx={{ padding: '8px 12px', color: 'text.onSurface.background.mediumContrast' }}>
                {t('messages.error_loading_chats')}
              </QText>
            )}
          </>
        )}
      </QBox>
      <QDivider />
      <QBox sx={{ padding: '8px 12px' }}>
        <QButton fullWidth size="small" variant="text" onClick={handleViewAllChats}>
          {t('button_labels.view_all_chats')}
        </QButton>
      </QBox>
    </QMenu>
  );
};
